import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, Subscription, of } from 'rxjs';
import { catchError, expand, map, reduce } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ContactDetailComponent } from '../../organization/contacts/contact-detail/contact-detail.component';
import { PaginationComponent } from '../../inputs/pagination/pagination.component';
import { EditContactModalComponent } from '../../forms/edit-contact-modal/edit-contact-modal.component';
import { ContactsService } from '../../../services/contacts/contacts.service';
import { GroupAudienceService, GroupRef, Page } from '../../../services/segments/group-audience.service';
import {
  IContact,
  IOrganization,
  IPagination,
  ISegment,
  ITag,
  TGroupType,
} from '../../../types/contactModule';
import { PromptCardComponent } from '../../toasts-and-cards/prompt-card/prompt-card.component';
import { EmptyStateComponent } from '../../ui/empty-state/empty-state.component';
import { DataTableComponent } from '../../ui/data-table/data-table.component';
import { SearchInputComponent } from '../../inputs/search-input/search-input.component';
import {
  SortDropdownComponent,
  SortDropdownGroup,
  buildSortGroups,
} from '../../inputs/sort-dropdown/sort-dropdown.component';
import { StatusComponent } from '../../ui/status/status.component';
import { HasPermissionDirective } from '../../../directives/has-permission.directive';
import { ContactRow, contactName, toContactRows } from '../contact-table/contact-row';
import { formatCount } from '../../../../utils/sms';
import { csvFileName, downloadCsv, toCsv } from '../../../../utils/csv';
import { GroupSelection, countNoun, humanizeEnum } from '../group-table/group-rows';

/** What the selection bar asks the details page to do. */
export type TMemberAction = 'send-sms' | 'remove' | 'move-to-organization' | 'add-tags' | 'delete';

type TMemberSort =
  | 'fullName,asc'
  | 'fullName,desc'
  | 'createdAt,asc'
  | 'createdAt,desc'
  | 'modifiedAt,asc'
  | 'modifiedAt,desc';

type TSearchField = 'fullName' | 'emailAddress' | 'primaryContactNumber' | 'country' | 'gender';

const SEARCH_PARAM: Record<TSearchField, string> = {
  fullName: 'fullName.contains',
  emailAddress: 'emailAddress.contains',
  primaryContactNumber: 'primaryContactNumber.contains',
  country: 'country.contains',
  gender: 'gender.equals',
};

/** The export pages through the members endpoint; past this many rows, narrow the list first. */
const EXPORT_CAP = 10000;
const EXPORT_PAGE_SIZE = 500;
/** Groups whose endpoint cannot search: load this many and filter here. */
const LOCAL_SEARCH_SIZE = 500;

interface MemberRow extends ContactRow {
  gender: string;
  country: string;
}

const GROUP_NOUN: Record<TGroupType, string> = {
  organization: 'organization',
  tag: 'tag',
  segment: 'segment',
};

@Component({
  selector: 'group-contacts-table',
  standalone: true,
  imports: [
    ContactDetailComponent,
    PaginationComponent,
    EditContactModalComponent,
    PromptCardComponent,
    EmptyStateComponent,
    DataTableComponent,
    SearchInputComponent,
    SortDropdownComponent,
    StatusComponent,
    HasPermissionDirective,
    MatTooltipModule,
  ],
  templateUrl: './group-contacts-table.component.html',
  styleUrls: [
    '../group-table/group-list.css',
    '../group-table/group-table.css',
    './group-contacts-table.component.css',
  ],
})
export class GroupContactsTableComponent implements OnChanges, OnDestroy {
  contactService = inject(ContactsService);
  private audience = inject(GroupAudienceService);
  private destroyRef = inject(DestroyRef);
  private host: ElementRef<HTMLElement> = inject(ElementRef);

  @ViewChild(SearchInputComponent) searchInput?: SearchInputComponent;

  @Output() updateSelectedContacts = new EventEmitter<IContact[]>();
  /** A selection bar action for the details page to carry out. */
  @Output() action = new EventEmitter<TMemberAction>();
  /** Remove one member from this organization or tag (row action). */
  @Output() removeContact = new EventEmitter<IContact>();
  /** Total members matching the current search, after each load. */
  @Output() totalChange = new EventEmitter<number>();
  /** A member was deleted or edited here; the page refreshes its counts. */
  @Output() membersChanged = new EventEmitter<void>();
  /** Extra query parameters merged into every request. */
  @Input() params: Record<string, any> = {};
  @Input({ required: true }) groupType!: TGroupType;
  @Input({ required: true }) groupDetails!: IOrganization | ITag | ISegment | null;

  readonly formatCount = formatCount;
  readonly exportCap = EXPORT_CAP;

  contacts: IContact[] = [];
  rows: MemberRow[] = [];
  pagination: IPagination = { totalElements: 0, pageSize: 10, totalPages: 1, pageNumber: 0, offSet: 0 };
  loading = true;
  loadError = '';
  hasLoaded = false;
  contactsPage: number = 0;
  totalPages: number = 0;
  pageSize: number = 10;
  private loadSub?: Subscription;

  // ── Search and sort ──
  searchTerm = '';
  private searchField: TSearchField = 'fullName';
  private searchFallback: TSearchField | null = null;
  sortCriteria: TMemberSort = 'fullName,asc';
  readonly sortGroups: SortDropdownGroup[] = buildSortGroups({
    nameField: 'fullName',
    createdField: 'createdAt',
    modifiedField: 'modifiedAt',
  });

  // ── Selection ──
  readonly selection = new GroupSelection<IContact>();

  // ── Row modals ──
  selectedContact: IContact | null = null;
  showContactDetail: boolean = false;
  openEditContactModal: boolean = false;
  showDeletePrompt: boolean = false;
  deleting = false;

  // ── Export ──
  exporting = false;
  exportDone = 0;
  exportTarget = 0;
  exportNotice = '';
  exportError = '';
  /** Screen-reader status: the export's start and end, not every page. */
  exportStatus = '';
  private exportSub?: Subscription;

  get ref(): GroupRef | null {
    const uuid = this.groupDetails?.uuid;
    if (!uuid) return null;
    return { kind: this.groupType, uuid, id: (this.groupDetails as { id?: string | number })?.id ?? null };
  }

  get groupNoun(): string {
    return GROUP_NOUN[this.groupType] ?? 'group';
  }

  get canRemove(): boolean {
    return this.groupType === 'organization' || this.groupType === 'tag';
  }

  get countLine(): string {
    if (!this.hasLoaded) return '';
    const total = this.pagination.totalElements ?? 0;
    const line = countNoun(total, 'member', 'members');
    return this.searchTerm.trim() ? `${line} ${total === 1 ? 'matches' : 'match'}` : line;
  }

  get selectionLabel(): string {
    return `${countNoun(this.selection.size, 'contact', 'contacts')} selected`;
  }

  get exportProgress(): number {
    return this.exportTarget ? Math.min(1, this.exportDone / this.exportTarget) : 0;
  }

  get exportLabel(): string {
    return this.searchTerm.trim() ? 'Export matches' : 'Export CSV';
  }

  get emptyMessage(): string {
    if (this.groupType === 'segment') return 'No contacts match these conditions';
    return `No contacts in this ${this.groupNoun} yet`;
  }

  get emptyHint(): string {
    if (this.groupType === 'segment') return 'Contacts appear here as soon as they match. Edit the conditions to widen the segment.';
    return 'Use Add contacts to put people in it.';
  }

  // ── Loading ──
  refresh() {
    this.contactsPage = 0;
    this.load();
  }

  load() {
    const ref = this.ref;
    if (!ref) return;
    this.loadSub?.unsubscribe();
    this.loading = true;
    this.loadError = '';

    const term = this.searchTerm.trim();
    const localSearch = !!term && !this.audience.searchesOnServer(ref);
    const params: Record<string, any> = {
      ...this.params,
      sort: this.sortCriteria,
      page: localSearch ? 0 : this.contactsPage,
      size: localSearch ? LOCAL_SEARCH_SIZE : this.pageSize,
    };
    if (term && !localSearch) {
      params[SEARCH_PARAM[this.searchField]] = this.searchField === 'gender' ? term.toUpperCase() : term;
    }

    this.loadSub = this.audience
      .membersPage(ref, params)
      .pipe(
        map((page) => (localSearch || (term && !this.audience.searchesOnServer(ref)) ? this.searchLocally(page, term) : page)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (page) => {
          const pagination = page.itemPagination ?? this.pagination;
          // A name search that found nobody: try the term as a country or gender.
          if (term && !localSearch && !pagination.totalElements && this.searchFallback && this.searchField === 'fullName') {
            this.searchField = this.searchFallback;
            this.searchFallback = null;
            this.load();
            return;
          }
          const lastPage = Math.max(0, (pagination.totalPages ?? 1) - 1);
          if (!page.items.length && this.contactsPage > lastPage && (pagination.totalElements ?? 0) > 0) {
            this.contactsPage = lastPage;
            this.load();
            return;
          }
          this.contacts = page.items;
          this.rows = this.toRows(page.items);
          this.pagination = { ...pagination, pageNumber: localSearch ? 0 : this.contactsPage };
          this.totalPages = this.pagination.totalPages;
          this.selection.refresh(page.items);
          this.loading = false;
          this.hasLoaded = true;
          this.totalChange.emit(pagination.totalElements ?? page.items.length);
        },
        error: () => {
          this.loading = false;
          this.loadError = 'Members did not load. Check your connection and try again.';
        },
      });
  }

  /** For endpoints that ignore search: keep the rows that match, as one page. */
  private searchLocally(page: Page<IContact>, term: string): Page<IContact> {
    const needle = term.toLowerCase();
    const items = page.items.filter((contact) => {
      const haystack = [
        contactName(contact),
        contact.primaryContactNumber,
        contact.emailAddress,
        contact.country,
      ]
        .map((value) => (value ?? '').toString().toLowerCase())
        .join(' ');
      return haystack.includes(needle) || (contact.gender ?? '').toLowerCase() === needle;
    });
    return {
      items,
      itemPagination: { totalElements: items.length, pageSize: items.length || this.pageSize, totalPages: 1, pageNumber: 0, offSet: 0 },
    };
  }

  private toRows(contacts: IContact[]): MemberRow[] {
    const base = toContactRows(contacts);
    return base.map((row) => ({
      ...row,
      gender: humanizeEnum(row.contact.gender) || '—',
      country: (row.contact.country ?? '').toString().trim() || '—',
    }));
  }

  // ── Search and sort ──
  updateSearchTerm(term: string) {
    this.searchTerm = term ?? '';
    const normalized = this.searchTerm.trim();
    this.searchField = 'fullName';
    this.searchFallback = null;
    if (normalized.includes('@')) {
      this.searchField = 'emailAddress';
    } else if (/^\+?\d+$/.test(normalized.replace(/[\s\-()]/g, ''))) {
      this.searchField = 'primaryContactNumber';
    } else if (normalized && this.groupType !== 'segment') {
      // The segment members endpoint keeps only name, phone and email search
      // (it drops country and gender, and would return every member), so
      // segments stop at the name search.
      this.searchFallback = ['MALE', 'FEMALE', 'OTHER'].includes(normalized.toUpperCase()) ? 'gender' : 'country';
    }
    this.refresh();
  }

  /** The empty state's Clear search. */
  clearSearch() {
    this.searchInput?.writeValue('');
    this.updateSearchTerm('');
  }

  updateSortCriteria(criteria: TMemberSort) {
    this.sortCriteria = criteria;
    this.refresh();
  }

  // ── Paging ──
  updateContactsPage(newPage: number) {
    this.contactsPage = Math.max(0, newPage);
    this.load();
  }
  goToNextPage() {
    this.updateContactsPage(this.contactsPage + 1);
  }
  goToPreviousPage() {
    this.updateContactsPage(this.contactsPage - 1);
  }
  /** The pager follows a size change with changePage(0), which loads. */
  onPageSizeChange(size: number) {
    this.pageSize = size;
  }

  // ── Selection ──
  get allContactsSelected(): boolean {
    return this.selection.allOn(this.contacts);
  }

  get someOnPageSelected(): boolean {
    return this.selection.someOn(this.contacts);
  }

  toggleCheckAll() {
    this.selection.togglePage(this.contacts);
    this.emitSelection();
  }

  checkedMember(contact: IContact) {
    return this.selection.has(contact);
  }

  select(contact: IContact) {
    this.selection.toggle(contact);
    this.emitSelection();
  }

  /** Clears every ticked member (the details pages call this after a bulk action). */
  unselectAll() {
    this.selection.clear();
    this.emitSelection();
    this.keepFocusOnPage();
  }

  /** Unticks just these members (after a row action removed them), leaving the rest ticked. */
  deselect(contacts: IContact[]) {
    if (!contacts?.length) return;
    this.selection.remove(contacts);
    this.emitSelection();
  }

  /**
   * The selection bar goes when nothing is ticked. If it held focus, move
   * focus to the search box rather than letting it drop to the page top.
   */
  private keepFocusOnPage() {
    setTimeout(() => {
      const active = this.host.nativeElement.ownerDocument?.activeElement;
      if (active && active !== active.ownerDocument.body) return;
      this.host.nativeElement.querySelector<HTMLInputElement>('.gl-search input')?.focus();
    });
  }

  emitSelection() {
    this.updateSelectedContacts.emit(this.selection.items);
  }

  runAction(action: TMemberAction) {
    if (!this.selection.size) return;
    this.action.emit(action);
  }

  // ── Row actions ──
  openRow(row: MemberRow) {
    this.openContactDetail(row.contact);
  }

  openFromKeyboard(event: Event, row: MemberRow) {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.openRow(row);
  }

  openContactDetail(contact: IContact) {
    this.selectedContact = contact;
    this.showContactDetail = true;
  }
  closeContactDetail() {
    this.showContactDetail = false;
  }

  openEditContact(contact: IContact) {
    this.selectedContact = contact;
    this.openEditContactModal = true;
  }
  closeEditContact() {
    this.openEditContactModal = false;
    // The edit may have changed the name, number or groups shown here.
    this.load();
    this.membersChanged.emit();
  }

  openDeletePrompt(contact: IContact) {
    this.selectedContact = contact;
    this.showDeletePrompt = true;
  }
  closeDeletePrompt() {
    if (this.deleting) return;
    this.showDeletePrompt = false;
  }

  get selectedName(): string {
    return contactName(this.selectedContact) || 'This contact';
  }

  /** Deletes the contact from the whole contact list, not just this group. */
  deleteContact() {
    const contact = this.selectedContact;
    if (!contact?.uuid || this.deleting) return;
    this.deleting = true;
    this.contactService.deleteContact(contact.uuid as string).subscribe({
      next: () => {
        this.deleting = false;
        this.showDeletePrompt = false;
        this.selection.remove([contact]);
        this.emitSelection();
        this.audience.invalidate(this.ref);
        this.load();
        this.membersChanged.emit();
      },
      error: () => {
        this.deleting = false;
        this.showDeletePrompt = false;
        this.contactService.showError(`${this.selectedName} was not deleted. Try again.`);
      },
    });
  }

  get contactLoading() {
    return this.contactService.isLoading;
  }

  // ── Export ──
  exportMembers() {
    const ref = this.ref;
    const total = this.pagination.totalElements ?? 0;
    if (!ref || this.exporting || !total) return;
    const term = this.searchTerm.trim();
    const base: Record<string, any> = { ...this.params, sort: this.sortCriteria };
    const serverSearch = this.audience.searchesOnServer(ref);
    if (term && serverSearch) {
      base[SEARCH_PARAM[this.searchField]] = this.searchField === 'gender' ? term.toUpperCase() : term;
    }

    this.exportTarget = Math.min(total, EXPORT_CAP);
    this.exportDone = 0;
    this.exportNotice = '';
    this.exportError = '';
    this.exportStatus = `Exporting ${countNoun(this.exportTarget, 'member', 'members')}.`;
    this.exporting = true;

    const page = (index: number): Observable<Page<IContact> & { index: number }> =>
      this.audience
        .membersPage(ref, { ...base, page: index, size: EXPORT_PAGE_SIZE })
        .pipe(map((res) => ({ index, ...(term && !serverSearch ? this.searchLocally(res, term) : res) })));

    let collected = 0;
    this.exportSub = page(0)
      .pipe(
        expand((res) => {
          collected += res.items.length;
          this.exportDone = Math.min(collected, this.exportTarget);
          const totalPages = res.itemPagination?.totalPages ?? 0;
          const more = res.items.length > 0 && res.index + 1 < totalPages && collected < this.exportTarget;
          return more ? page(res.index + 1) : EMPTY;
        }),
        reduce((all: IContact[], res) => all.concat(res.items), []),
        catchError(() => {
          this.exportError = 'The export stopped before it finished. Check your connection and try again.';
          this.exportStatus = '';
          this.exporting = false;
          return of(null);
        })
      )
      .subscribe((all) => {
        if (!all) return;
        const kept = all.slice(0, EXPORT_CAP);
        const name = (this.groupDetails?.name ?? this.groupNoun).toString();
        downloadCsv(csvFileName(name, 'members', term ? 'search' : null), this.toMembersCsv(kept));
        this.exporting = false;
        if (total > EXPORT_CAP) {
          this.exportNotice = `Exported the first ${formatCount(EXPORT_CAP)} of ${formatCount(total)} members. Narrow the list with search to export the rest.`;
          this.exportStatus = this.exportNotice;
        } else {
          // The toast says it.
          this.exportStatus = '';
          this.contactService.showSuccess(`Exported ${countNoun(kept.length, 'member', 'members')}.`);
        }
      });
  }

  cancelExport() {
    this.exportSub?.unsubscribe();
    this.exporting = false;
    this.exportDone = 0;
    this.exportStatus = 'Export cancelled.';
  }

  dismissExportNotice() {
    this.exportNotice = '';
    this.exportError = '';
  }

  private toMembersCsv(rows: IContact[]): string {
    const names = (items: { name?: string | null }[] | undefined) =>
      (items ?? []).map((item) => item?.name).filter(Boolean).join('; ');
    return toCsv(rows, [
      { label: 'First name', value: (c) => c.firstName },
      { label: 'Last name', value: (c) => c.lastName },
      { label: 'Phone number', value: (c) => c.primaryContactNumber },
      { label: 'Email', value: (c) => c.emailAddress },
      { label: 'Gender', value: (c) => c.gender },
      { label: 'Birth date', value: (c) => (c.birthDate ? String(c.birthDate).slice(0, 10) : '') },
      { label: 'City', value: (c) => c.city },
      { label: 'Country', value: (c) => c.country },
      { label: 'Organizations', value: (c) => names(c.companyEntities) },
      { label: 'Tags', value: (c) => names(c.entityTags) },
      { label: 'Added', value: (c) => c.createdAt },
    ]);
  }

  ngOnChanges(changes: SimpleChanges) {
    const before = (changes['groupDetails']?.previousValue as { uuid?: string } | null)?.uuid;
    const after = this.groupDetails?.uuid;
    if (changes['groupDetails'] && after && before !== after) {
      if (this.selection.size) {
        this.selection.clear();
        this.emitSelection();
      }
      this.hasLoaded = false;
      this.refresh();
    } else if (changes['params'] && !changes['params'].firstChange && after) {
      this.refresh();
    }
  }

  ngOnDestroy() {
    this.exportSub?.unsubscribe();
  }
}
